import { useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { 
  Box, 
  TextField, 
  IconButton, 
  Tooltip, 
  CircularProgress, 
  LinearProgress 
} from '@mui/material';
import { Send, AttachFile, AutoAwesome } from '@mui/icons-material';
import { getSocket } from 'state/socket';


const ChatMessageInput = ({ conversationId, onSend }) => {
  const user = useSelector((state) => state.user);

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const typingTimerRef = useRef(null);
  const fileInputRef = useRef(null);


  const emitTyping = useCallback(() => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit('chat:typing', { conversationId, userId: user._id });
    clearTimeout(typingTimerRef.current);
  }, [conversationId, user._id]);

  const handleTextChange = (e) => {
    setText(e.target.value);
    emitTyping();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (file.size > 20 * 1024 * 1024) {
      toast.error('File size must be under 20 MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/messages/upload', formData);

      setPendingAttachments((prev) => [...prev, res.data]);
    } catch (err) {
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleSend = async () => {
    if ((!text.trim() && pendingAttachments.length === 0) || sending) return;

    const type =
      pendingAttachments.length > 0
        ? pendingAttachments[0].type === 'image'
          ? 'image'
          : 'file'
        : 'text';

    try {
      setSending(true);
      const payload = {
        conversationId,
        content: text.trim(),
        type,
        attachments: pendingAttachments.map(({ url, name, size, mimeType }) => ({
          url, name, size, mimeType,
        })),
      };

      const res = await api.post('/messages', payload);
      onSend?.(res.data);
      setText('');
      setPendingAttachments([]);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      {uploading && <LinearProgress sx={{ mb: 0.5, borderRadius: 1 }} />}

      {/* Pending attachment previews */}
      {pendingAttachments.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 0.75 }}>
          {pendingAttachments.map((att, i) =>
            att.type === 'image' ? (
              <Box
                key={i}
                component="img"
                src={att.url}
                alt={att.name}
                onClick={() => setPendingAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                sx={{ 
                  height: 56, 
                  width: 56, 
                  objectFit: 'cover', 
                  borderRadius: 1, 
                  cursor: 'pointer' 
                }}
              />
            ) : (
              <Box
                key={i}
                display="flex"
                alignItems="center"
                gap={0.5}
                onClick={() => setPendingAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                sx={{
                  px: 1, 
                  py: 0.5, 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'divider', 
                  fontSize: '0.75rem', 
                  cursor: 'pointer',
                }}
              >
                <AttachFile fontSize="small" />
                {att.name}
              </Box>
            )
          )}
        </Box>
      )}

      <Box 
        display="flex" 
        alignItems="center" 
        gap={2}
      >
        <input
          hidden
          id="upload-file"          
          type="file"
          ref={fileInputRef}          
          onChange={handleFileChange}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
        />
        <label htmlFor="upload-file" style={{ display: 'none' }}>
          <Tooltip title="Attach file">
            <IconButton 
              size="small" 
              onClick={() => fileInputRef.current?.click()} 
              disabled={uploading}
            >
              <AttachFile fontSize="small" />
            </IconButton>
          </Tooltip>
        </label>

        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          variant="outlined"
          placeholder="Type a message …"
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          disabled={sending}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
        />

        <Tooltip title="Ask AI (@AI)">
          <IconButton
            size="small"
            onClick={() => setText((prev) => (prev.startsWith('@AI') ? prev : `@AI ${prev}`))}
            sx={{ display: "none", color: 'primary.main' }}
          >
            <AutoAwesome fontSize="small" />
          </IconButton>
        </Tooltip>

        <IconButton
          size="small"
          color="primary"
          onClick={handleSend}
          disabled={sending || (!text.trim() && pendingAttachments.length === 0)}
        >
          {sending ? <CircularProgress size={18} /> : <Send fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatMessageInput;
